<script setup lang="ts">
import { ref } from 'vue';

import type {
  ReviewWidgetBundle,
} from '@thehotelsnetwork/personalization-types';
import {
  RecordGrid,
  Button,
  Collapse,
  useTheme,
  useI18n,
  useTextDirection,
  useInlinerWidth,
} from '@thehotelsnetwork/widgets-core';

import MainRating from '../components/MainRating.vue';
import PropertyRanking from '../components/PropertyRanking.vue';
import ProvidersInfo from '../components/ProvidersInfo.vue';
import ReviewsModal from './ReviewsModal.vue';
import {
  useProvidersItems,
  useRankingConfig,
} from '../composables';

const props = defineProps<{
  config: ReviewWidgetBundle;
}>();

const theme = useTheme();
const { t } = useI18n();
const showWidget = ref(false);
const textDirection = useTextDirection(props.config.isRightAlign);

const modalOpen = ref(false);

const providersItems = useProvidersItems(
  props.config.payload.reviewProviders,
);
const propertyRankingConfig = useRankingConfig(props.config);
const width = useInlinerWidth({
  width: props.config.deviceStyleConfig.extra?.width,
  widthUnit: props.config.deviceStyleConfig.extra?.widthUnit,
});
</script>

<template>
  <Collapse
    v-model="showWidget"
    auto-open
    class="root"
  >
    <div class="root__inner">
      <div
        :class="[
          'root widget widget--inline',
          { 'widget--bordered': theme.border },
        ]"
        data-testid="widget"
      >
        <div
          :class="[
            'module mainRating separator',
            props.config.isRightAlign ? 'separator--before' : 'separator--after',
          ]"
        >
          <MainRating
            :data="config.payload.reviewAverages"
            :colors="{
              darkText: theme.backgroundColor.contrasted,
              lightText: theme.backgroundColor.contrasted,
            }"
            orientation="horizontal"
            show-info-btn
            class="mainRating__content"
          >
            <template #providersInfo>
              <ProvidersInfo
                :data="providersItems"
                :direction="textDirection"
              />
            </template>
          </MainRating>
        </div>
        <div
          v-if="propertyRankingConfig.visible"
          :class="[
            'module ranking separator',
            props.config.isRightAlign ? 'separator--before' : 'separator--after',
          ]"
        >
          <PropertyRanking
            :config="propertyRankingConfig"
            :hightlight-color="theme.actionContrastColor.base"
            inline
            class="ranking__content"
          />
        </div>
        <div class="module providerRating">
          <RecordGrid
            :items="providersItems.slice(0, 5)"
            direction="row"
          >
            <template #value="{ item }">
              <span class="providerRating__score">
                {{ item.value.score }}
              </span>
              <span class="providerRating__max">
                /{{ item.value.max }}
              </span>
            </template>
          </RecordGrid>
        </div>
        <div
          v-if="props.config.showReviews"
          class="module cta flex justify-end shrink-0"
        >
          <Button
            @click="modalOpen = true"
            :color-scheme="{
              main: theme.actionContrastColor.base,
              text: theme.actionContrastColor.base,
            }"
            class="trigger text-sm"
            data-testid="read-reviews"
          >
            <span class="text-sm">
              {{ t('Read Reviews') }}
            </span>
          </Button>
        </div>
        <Teleport :to="props.config.modalRootContainer ?? 'body'">
          <Transition name="fade">
            <ReviewsModal
              v-if="modalOpen"
              :config="config"
              @close="modalOpen = false"
              :style="{ 'z-index': $props.config.zIndex }"
            />
          </Transition>
        </Teleport>
      </div>
    </div>
  </Collapse>
</template>

<style scoped lang="postcss">
.root {
  width: v-bind('width');
}
.root__inner {
  container-type: inline-size;
  width: 100%;
}
.widget {
  display: flex;
  flex-direction: row;
  width: 100%;
  @apply py-4 px-6;
  border-radius: v-bind('theme.borderRadius');
  background: v-bind('theme.backgroundColor.base');
  &--bordered {
    border: 1px solid v-bind('theme.borderColor');
  }
}
.module {
  @apply flex items-center;
}
.mainRating__content {
  flex: 1 1 0%;
  padding-inline-end: 24px;
}
.ranking,
.cta {
  color: v-bind('theme.backgroundColor.contrasted');
}
.ranking {
  width: 17%;
  max-width: 20rem;
  min-width: 10rem;
  @apply justify-center;
}
.ranking__content {
  @apply px-6;
}

.providerRating {
  flex: 1;
  width: 100%;
  @apply px-6;
  color: v-bind('theme.backgroundColor.contrasted');
  &__score {
    @apply font-semibold text-xl leading-3;
  }
}

.separator {
  @apply relative;
  &:before,
  &:after {
    @apply absolute top-0 h-full;
    width: 1px;
    background-color: v-bind('theme.backgroundColor.contrasted');
  }
}
.separator--before {
  &:before {
    content: '';
    @apply left-0;
  }
}
.separator--after {
  &:after {
    content: '';
    @apply right-0;
  }
}

@container (max-width: 720px) {
  .widget {
    @apply flex-col items-center p-4 gap-4;
    .mainRating__content {
      @apply pr-0 justify-center;
    }
    .ranking {
      background: rgba(255,255,255,0.24);
      width: 95%;
      max-width: none;
      @apply justify-center p-3 rounded-lg;
    }
    .ranking__content {
      @apply pr-0;
    }
    .separator {
      &:after,
      &:before {
        @apply hidden
      }
    }
    .providerRating {
      @apply px-0;
    }
    .cta {
      @apply w-full;
      .trigger {
        @apply w-full
      }
    }
  }
}
</style>
